import { Heart } from 'lucide-react';
import PortfolioHealthSnapshot from '@/components/PortfolioHealthSnapshot';
import HealthScoreTrend from '@/components/HealthScoreTrend';
import usePortfolioContrastRows from '@/hooks/usePortfolioContrastRows';
import useCpiYoy from '@/hooks/useCpiYoy';

// Standalone Conviction Snapshot card (Sep 2026) — replaces the section
// that used to live inside the retired Income by Portfolio card. One
// snapshot per visible portfolio row; the header matches the other
// main-card titles on the Portfolio page (Benchmark Comparison etc).
export default function ConvictionSnapshotCard({ onStockClick }) {
  const { rows, loaded, isAllView } = usePortfolioContrastRows();
  const cpiYoy = useCpiYoy();
  if (!loaded) return null;
  const withSnapshot = rows.filter((r) => r.health_snapshot?.total_analyzed);
  if (withSnapshot.length === 0) return null;

  return (
    <div className="rounded-lg border bg-card p-4 mb-3" data-testid="conviction-snapshot-card">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h3 className="text-base font-extrabold flex items-center gap-2">
          <Heart className="w-4 h-4 text-rose-500 shrink-0" />
          Conviction Snapshot
        </h3>
      </div>
      <p className="text-[11px] text-muted-foreground">
        How many of {isAllView && withSnapshot.length > 1 ? 'each portfolio\u2019s' : 'this portfolio\u2019s'} positions pass DiviDrip&apos;s 5-point Buy Checklist.
      </p>
      {withSnapshot.map((row) => (
        <PortfolioHealthSnapshot
          key={row.portfolio_id || 'untagged'}
          title={isAllView && withSnapshot.length > 1 ? row.name : null}
          snapshot={row.health_snapshot}
          onStockClick={onStockClick}
          testIdPrefix={`conviction-${row.portfolio_id || 'untagged'}`}
          trend={isAllView ? null : { portfolioId: row.portfolio_id || null }}
          healthScoreInputs={{
            portfolioYield: Number(row.portfolio_yield) || null,
            cpiYoy: typeof cpiYoy === 'number' ? cpiYoy : null,
            fcfCoverage: row.health_rollup?.weighted_div_fcf_coverage ?? null,
            revenueYoY: row.health_rollup?.weighted_revenue_yoy ?? null,
            concentrationPct: row.concentration?.max_concentration?.pct ?? null,
          }}
        />
      ))}
      {/* All Portfolios view — one combined trend for every portfolio added together. */}
      {isAllView && (
        <div className="mt-3" data-testid="conviction-all-trend-wrapper">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-1">All portfolios combined</p>
          <HealthScoreTrend portfolioId={null} testIdPrefix="conviction-all" />
        </div>
      )}
    </div>
  );
}
